import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import SwipeableViews from 'react-swipeable-views';
import { Box, Grid } from '@material-ui/core';
import { useStyles } from './styles';
import { MediaItem } from '../MediaItem/MediaItem';
import { MediaItemFragmentSource } from '../PublicPropertyOverlay/PropertyOverlay';

type ImageSliderProps = {
    images: MediaItemFragmentSource[];
    showTitle?: boolean;
    showCount?: boolean;
    size?: 1 | 2;
    onActiveStepChange?: (activeStep: number) => void;
    arrowsOut?: boolean;
    initialActiveStep?: number;
}

export const ImageSlider = ({
    images,
    showTitle = false,
    size = 1,
    showCount = false,
    onActiveStepChange,
    arrowsOut = false,
    initialActiveStep = 0,
}: ImageSliderProps) => {
    const classes = useStyles({ multiplier: size, arrowsOut });
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(initialActiveStep);
    const maxSteps = images.length;

    useEffect(() => {
        setActiveStep(initialActiveStep);
    }, [images, initialActiveStep]);

    useMemo(() => {
        if (activeStep > maxSteps) {
            setActiveStep(initialActiveStep);
        }
    }, [maxSteps, activeStep, initialActiveStep]);

    useEffect(() => {
        if (onActiveStepChange) {
            onActiveStepChange(activeStep);
        }
    }, [activeStep, onActiveStepChange]);

    const handleNext = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        let next = activeStep + 1;
        if (next >= maxSteps) {
            next = 0;
        }
        setActiveStep(next);
    };

    const handleBack = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        let prev = activeStep - 1;
        if (prev < 0) {
            prev = maxSteps - 1;
        }
        setActiveStep(prev);
    };

    const handleStepChange = (step: number) => {
        setActiveStep(step);
    };

    return (
        <div className={classes.root}>
            {images[activeStep] && (
                <Box className={classes.sliderContianer}>
                    {showTitle && images[activeStep].title && (
                        <Box className={classes.titleContainer}>
                            <Typography
                                className={classes.title}
                                component="span"
                                variant="body2"
                                color="primary"
                            >
                                {images[activeStep].title}
                            </Typography>
                        </Box>
                    )}
                    <SwipeableViews
                        axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                        index={activeStep}
                        onChangeIndex={handleStepChange}
                        enableMouseEvents
                    >
                        {images.map((image, index) => (
                            <div key={`${image.location}-${index - 1}`} className={classes.imgContainer}>
                                {Math.abs(activeStep - index) <= 2
                                    ? (
                                        <MediaItem
                                            className={image.type === 'image' ? classes.img : classes.embed}
                                            item={image}
                                            background
                                        />
                                    )
                                    : null}
                            </div>
                        ))}
                    </SwipeableViews>
                    <Button className={classes.prevButton} size="small" variant="contained" onClick={handleBack}>
                        <KeyboardArrowLeft />
                    </Button>
                    <Button className={classes.nextButton} size="small" variant="contained" onClick={handleNext}>
                        <KeyboardArrowRight />
                    </Button>
                    <Box className={classes.indicatorContainer}>
                        <Grid container justifyContent="center">
                            {images.map((image, index) => (
                                <Grid
                                    item
                                    onClick={() => handleStepChange(index)}
                                    key={`${image.location}-${index - 1}`}
                                >
                                    <div
                                        className={`
                                            ${classes.indicator}
                                            ${activeStep === index ? classes.selected : ''}
                                        `}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    {images[activeStep].source && (
                        <Box className={classes.sourceContainer}>
                            <Typography variant="body2">
                                {`${images[activeStep].source}`}
                            </Typography>
                        </Box>
                    )}
                    {showCount && (
                        <Box className={classes.numberContainer}>
                            <Typography variant="body2">
                                {`${activeStep + 1}`.padStart(2, '0')}
                                {' '}
                                /
                                {' '}
                                {`${maxSteps}`.padStart(2, '0')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </div>
    );
};
